/**
 * Group voice inside a room, in the browser.
 *
 * A full mesh of WebRTC audio connections (at most VOICE_MAX_PARTICIPANTS players). Audio is encrypted
 * (DTLS-SRTP) and flows directly between players; the server only keeps the participant list and relays
 * signalling between members of the same room. Nothing is recorded.
 *
 * - Joining only listens. The microphone is requested the first time the player turns it on.
 * - For each pair of players one side (the one with the smaller id) makes the offer and the other only answers.
 *   Offers therefore never collide, and each connection carries exactly one audio section.
 * - A moderator's mute is applied here too: the player's microphone is switched off and cannot be re-enabled.
 */
import type { CallSignal, ServerMsg, VoiceParticipant } from "@samra/shared";
import { useSyncExternalStore } from "react";
import { get, send, subscribe } from "./api";

export type LinkState = "connecting" | "connected" | "reconnecting" | "failed";

export interface VoiceState {
  roomId: string | null;
  /** Session id for this join. A rejoin with the same id keeps existing connections. */
  sid: string;
  micOn: boolean;
  error: string | null;
  max: number;
  participants: VoiceParticipant[];
  /** Connection state towards each other participant, as this player sees it. */
  links: Record<string, LinkState>;
  /** Who is speaking right now, as heard by this player (the local microphone is under the player's id). */
  speaking: Record<string, boolean>;
}

const EMPTY: VoiceState = { roomId: null, sid: "", micOn: false, error: null, max: 4, participants: [], links: {}, speaking: {} };
let state: VoiceState = EMPTY;
const listeners = new Set<() => void>();
const set = (p: Partial<VoiceState>) => {
  state = { ...state, ...p };
  listeners.forEach((f) => f());
};

export const getVoice = () => state;
export function subscribeVoice(f: () => void) {
  listeners.add(f);
  return () => listeners.delete(f);
}
/** React hook: the current voice state, re-rendered on every change. */
export function useVoice(): VoiceState {
  return useSyncExternalStore(subscribeVoice, getVoice);
}

interface Peer {
  userId: string;
  sid: string;
  pc: RTCPeerConnection;
  /** Only the initiator sends offers (and ICE restarts). */
  initiator: boolean;
  /** The audio section that carries this player's microphone (set once the section exists). */
  transceiver: RTCRtpTransceiver | null;
  audio: HTMLAudioElement | null;
  pending: RTCIceCandidateInit[];
  restartTimer: ReturnType<typeof setTimeout> | null;
  analyser: AnalyserNode | null;
  source: MediaStreamAudioSourceNode | null;
}

const peers = new Map<string, Peer>();
/** Signals that arrived before the roster created their peer (messages from different sockets can overtake). */
const early = new Map<string, { sid: string; data: CallSignal }[]>();
let me = "";
let mic: MediaStream | null = null;
let micSource: MediaStreamAudioSourceNode | null = null;
let audioCtx: AudioContext | null = null;
let meter: ReturnType<typeof setInterval> | null = null;
let iceCache: { servers: RTCIceServer[]; until: number } | null = null;

const SPEAK_LEVEL = 0.03;

/** Joins (or switches to) a room's voice chat. Listening starts at once; the microphone stays off. */
export async function joinVoice(roomId: string, myId: string): Promise<void> {
  if (state.roomId === roomId) return;
  if (state.roomId) leaveVoice();
  me = myId;
  const sid = (crypto.randomUUID?.() ?? Math.random().toString(36).slice(2)).replace(/[^A-Za-z0-9_-]/g, "").padEnd(8, "x");
  set({ roomId, sid, error: null, participants: [], links: {}, speaking: {}, micOn: false });
  // Created inside the click that starts the call, so browsers allow audio playback.
  try {
    audioCtx ??= new AudioContext();
    void audioCtx.resume().catch(() => {});
  } catch {
    audioCtx = null;
  }
  ensureMeter();
  // Load TURN/STUN first, so the connections created for other players already use them.
  await iceServersAsync();
  if (state.roomId !== roomId || state.sid !== sid) return;
  send({ t: "voice.join", roomId, sid });
}

/** Leaves voice: releases the microphone, closes every connection and tells the server. */
export function leaveVoice(): void {
  const roomId = state.roomId;
  if (!roomId) return;
  send({ t: "voice.leave", roomId });
  closeAll();
  stopMic();
  if (meter) clearInterval(meter);
  meter = null;
  set({ roomId: null, sid: "", participants: [], links: {}, speaking: {}, micOn: false, error: null });
}

/** Leaves voice only if it is for this room (used when the room page closes). */
export function leaveVoiceFor(roomId: string): void {
  if (state.roomId === roomId) leaveVoice();
}

/** Turns the player's microphone on or off. Turning it on asks the browser for permission. */
export async function toggleMic(): Promise<void> {
  const roomId = state.roomId;
  if (!roomId) return;
  if (state.micOn) {
    stopMic();
    send({ t: "voice.mute", roomId, muted: true });
    return;
  }
  const mine = state.participants.find((p) => p.userId === me);
  if (mine?.serverMuted) return set({ error: "voice_muted_by_moderator" });
  let stream: MediaStream;
  try {
    stream = await getMicrophone();
  } catch (e: any) {
    return set({ error: e.code ?? "mic_unavailable" });
  }
  // The player may have left, or been muted, while the browser was asking for permission.
  if (state.roomId !== roomId || state.participants.find((p) => p.userId === me)?.serverMuted) {
    stream.getTracks().forEach((t) => t.stop());
    return;
  }
  mic = stream;
  const track = stream.getAudioTracks()[0];
  for (const p of peers.values()) void p.transceiver?.sender.replaceTrack(track).catch(() => {});
  set({ micOn: true, error: null });
  send({ t: "voice.mute", roomId, muted: false });
  ensureMeter();
}

/** Server messages that drive group voice. Returns true when the message belonged to voice. */
export function handleVoiceMessage(m: ServerMsg): boolean {
  switch (m.t) {
    case "welcome": {
      // The socket reconnected: rejoin with the same session id so existing connections survive.
      if (state.roomId) send({ t: "voice.join", roomId: state.roomId, sid: state.sid });
      return false;
    }
    case "voice.roster": {
      if (m.roomId !== state.roomId) return true;
      applyRoster(m.participants, m.max);
      return true;
    }
    case "voice.signal": {
      if (m.roomId !== state.roomId) return true;
      void applySignal(m.from, m.sid, m.data);
      return true;
    }
    case "room.removed": {
      if (m.roomId === state.roomId) leaveVoice();
      return false;
    }
    case "error": {
      if (m.ref !== "voice" || !state.roomId) return false;
      // A refused join leaves us outside the voice chat, with the reason shown.
      if (["not_in_room", "voice_full", "blocked", "bad_sid", "bad_room"].includes(m.code) && !state.participants.some((p) => p.userId === me)) {
        closeAll();
        stopMic();
        set({ roomId: null, sid: "", participants: [], links: {}, speaking: {}, micOn: false, error: m.code });
        return true;
      }
      set({ error: m.code });
      return true;
    }
    default:
      return false;
  }
}

/** Wires voice to the socket once. Safe to call repeatedly. */
let wired = false;
export function wireVoice(): void {
  if (wired) return;
  wired = true;
  subscribe((m) => {
    handleVoiceMessage(m);
  });
}

function applyRoster(participants: VoiceParticipant[], max: number) {
  const mine = participants.find((p) => p.userId === me);
  if (!mine) {
    // The server no longer has us in voice (left, kicked or the room closed).
    if (state.roomId) leaveVoice();
    return;
  }
  if (mine.serverMuted && state.micOn) {
    // Cooperative enforcement: switch off the microphone, and tell the server our state.
    stopMic();
    send({ t: "voice.mute", roomId: state.roomId, muted: true });
  }
  const others = participants.filter((p) => p.userId !== me);
  const present = new Set(others.map((p) => p.userId));
  for (const id of [...peers.keys()]) if (!present.has(id)) closePeer(id);
  for (const p of others) ensurePeer(p);
  set({ participants, max });
}

/** For each pair of players, exactly one side offers: the one with the smaller id. */
function peerIsInitiator(peerId: string) {
  return me < peerId;
}

function ensurePeer(p: VoiceParticipant) {
  const old = peers.get(p.userId);
  if (old && old.sid === p.sid) return;
  if (old) closePeer(p.userId);
  const pc = new RTCPeerConnection({ iceServers: iceCache?.servers ?? [] });
  // The initiator creates its audio section before offering. The answering side takes the section that
  // the offer creates: a section created earlier is not matched by the offer, so it would never be sent.
  const transceiver = peerIsInitiator(p.userId) ? pc.addTransceiver("audio", { direction: "sendrecv" }) : null;
  const peer: Peer = {
    userId: p.userId,
    sid: p.sid,
    pc,
    initiator: peerIsInitiator(p.userId),
    transceiver,
    audio: null,
    pending: [],
    restartTimer: null,
    analyser: null,
    source: null,
  };
  peers.set(p.userId, peer);
  if (!iceCache || iceCache.until <= Date.now()) {
    void iceServersAsync().then((servers) => {
      if (peers.get(p.userId) !== peer || !servers.length) return;
        try {
          pc.setConfiguration({ iceServers: servers });
        } catch {
          // The connection keeps working with the servers it already has.
        }
    });
  }
  setLink(p.userId, "connecting");
  if (mic && transceiver) void transceiver.sender.replaceTrack(mic.getAudioTracks()[0]).catch(() => {});

  pc.onnegotiationneeded = async () => {
    // Answering side: its own negotiation-needed event is ignored; the initiator's offer drives the exchange.
    if (!peer.initiator) return;
    try {
      await pc.setLocalDescription();
      send({ t: "voice.signal", roomId: state.roomId, to: p.userId, data: { kind: "offer", sdp: pc.localDescription!.sdp } });
    } catch {
      // A later negotiation attempt will retry.
    }
  };
  pc.onicecandidate = (e) => {
    send({ t: "voice.signal", roomId: state.roomId, to: p.userId, data: { kind: "candidate", candidate: e.candidate ? e.candidate.toJSON() : null } });
  };
  pc.ontrack = (e) => {
    const audio = document.createElement("audio");
    audio.autoplay = true;
    audio.srcObject = e.streams[0] ?? new MediaStream([e.track]);
    audio.setAttribute("aria-hidden", "true");
    document.body.appendChild(audio);
    void audio.play().catch(() => {});
    peer.audio = audio;
    attachMeter(peer, audio.srcObject as MediaStream);
  };
  pc.onconnectionstatechange = () => {
    const s = pc.connectionState;
    if (peers.get(p.userId) !== peer) return;
    if (s === "connected") {
      if (peer.restartTimer) clearTimeout(peer.restartTimer);
      peer.restartTimer = null;
      setLink(p.userId, "connected");
    } else if (s === "connecting" || s === "new") {
      setLink(p.userId, "connecting");
    } else if (s === "disconnected") {
      setLink(p.userId, "reconnecting");
      if (peer.initiator && !peer.restartTimer) peer.restartTimer = setTimeout(() => pc.restartIce(), 3000);
    } else if (s === "failed") {
      setLink(p.userId, "failed");
      if (peer.initiator) pc.restartIce();
    }
  };
  // Deliver anything that arrived for this player before their peer existed (handlers are in place now).
  const queued = (early.get(p.userId) ?? []).filter((x) => x.sid === p.sid);
  early.delete(p.userId);
  for (const x of queued) void applySignal(p.userId, x.sid, x.data);
}

async function applySignal(from: string, sid: string, data: CallSignal) {
  const peer = peers.get(from);
  if (!peer) {
    const list = early.get(from) ?? [];
    if (list.length < 50) early.set(from, [...list, { sid, data }]);
    return;
  }
  if (peer.sid !== sid) return; // stale session
  const pc = peer.pc;
  try {
    if (data.kind === "offer" || data.kind === "answer") {
      // The initiator never answers an offer, and only the initiator accepts answers. Anything else is stale.
      if (data.kind === "offer" && peer.initiator) return;
      if (data.kind === "answer" && !peer.initiator) return;
      await pc.setRemoteDescription({ type: data.kind, sdp: data.sdp });
      if (data.kind === "offer" && !peer.transceiver) {
        // The offer created this side's audio section: send and receive, and use the microphone if it is on.
        const t = pc.getTransceivers().find((x) => x.receiver.track.kind === "audio");
        if (t) {
          t.direction = "sendrecv";
          peer.transceiver = t;
          const track = mic?.getAudioTracks()[0];
          if (track) void t.sender.replaceTrack(track).catch(() => {});
        }
      }
      for (const c of peer.pending.splice(0)) await pc.addIceCandidate(c).catch(() => {});
      if (data.kind === "offer") {
        await pc.setLocalDescription();
        send({ t: "voice.signal", roomId: state.roomId, to: from, data: { kind: "answer", sdp: pc.localDescription!.sdp } });
      }
      return;
    }
    if (data.kind === "candidate" && data.candidate) {
      if (!pc.remoteDescription) peer.pending.push(data.candidate as RTCIceCandidateInit);
      else await pc.addIceCandidate(data.candidate as RTCIceCandidateInit).catch(() => {});
    }
  } catch {
    // Malformed or out-of-order signalling is dropped; the next negotiation repairs it.
  }
}

function setLink(userId: string, link: LinkState) {
  set({ links: { ...state.links, [userId]: link } });
}

function closePeer(userId: string) {
  const p = peers.get(userId);
  if (!p) return;
  if (p.restartTimer) clearTimeout(p.restartTimer);
  p.pc.close();
  p.audio?.remove();
  p.source?.disconnect();
  peers.delete(userId);
  const links = { ...state.links };
  delete links[userId];
  const speaking = { ...state.speaking };
  delete speaking[userId];
  set({ links, speaking });
}

function closeAll() {
  for (const id of [...peers.keys()]) closePeer(id);
}

function stopMic() {
  mic?.getTracks().forEach((t) => t.stop());
  mic = null;
  micSource?.disconnect();
  micSource = null;
  localAnalyser = null;
  for (const p of peers.values()) void p.transceiver?.sender.replaceTrack(null).catch(() => {});
  if (state.micOn) set({ micOn: false });
}

/** Microphone for voice: the same permission rules as one-to-one calls. */
async function getMicrophone(): Promise<MediaStream> {
  if (!window.isSecureContext) throw Object.assign(new Error("requires_https"), { code: "requires_https" });
  if (!navigator.mediaDevices?.getUserMedia) throw Object.assign(new Error("unsupported_browser"), { code: "unsupported_browser" });
  try {
    return await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true }, video: false });
  } catch (e: any) {
    const code = e?.name === "NotAllowedError" || e?.name === "SecurityError" ? "mic_permission_denied" : e?.name === "NotFoundError" || e?.name === "OverconstrainedError" ? "no_microphone" : "mic_unavailable";
    throw Object.assign(new Error(code), { code });
  }
}

/** ICE servers from the server (STUN, plus short-lived TURN credentials when configured). */
async function iceServersAsync(): Promise<RTCIceServer[]> {
  if (iceCache && iceCache.until > Date.now()) return iceCache.servers;
  try {
    const r = await get<{ iceServers: RTCIceServer[]; expiresAt: string }>("/api/calls/ice");
    iceCache = { servers: r.iceServers, until: Date.parse(r.expiresAt) - 5 * 60_000 };
    return r.iceServers;
  } catch {
    return [];
  }
}

/* ───────── who is speaking ───────── */

function attachMeter(peer: Peer, stream: MediaStream) {
  if (!audioCtx || !stream.getAudioTracks().length) return;
  peer.source?.disconnect();
  peer.source = audioCtx.createMediaStreamSource(stream);
  peer.analyser = audioCtx.createAnalyser();
  peer.analyser.fftSize = 512;
  peer.source.connect(peer.analyser);
}

function level(a: AnalyserNode): number {
  const buf = new Uint8Array(a.fftSize);
  a.getByteTimeDomainData(buf);
  let sum = 0;
  for (const v of buf) {
    const x = (v - 128) / 128;
    sum += x * x;
  }
  return Math.sqrt(sum / buf.length);
}

function ensureMeter() {
  if (meter) return;
  meter = setInterval(() => {
    const speaking: Record<string, boolean> = {};
    for (const p of peers.values()) if (p.analyser) speaking[p.userId] = level(p.analyser) > SPEAK_LEVEL;
    if (state.micOn && micSource === null && mic && audioCtx) {
      micSource = audioCtx.createMediaStreamSource(mic);
      const a = audioCtx.createAnalyser();
      a.fftSize = 512;
      micSource.connect(a);
      localAnalyser = a;
    }
    if (localAnalyser && state.micOn) speaking[me] = level(localAnalyser) > SPEAK_LEVEL;
    const before = JSON.stringify(state.speaking);
    const after = JSON.stringify(speaking);
    if (before !== after) set({ speaking });
  }, 250);
}
let localAnalyser: AnalyserNode | null = null;
